import clsx from "clsx";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import type { CaseReview as ApiCaseReview } from "@/services/api/case-checking";
import { CC_PANEL, CC_SECTION_LABEL } from "@/components/case-checking/caseCheckingChrome";
import type { ReviewAttribution } from "@/components/case-checking/useHumanReviewOutcome";
import { AgentHumanGradeCompare, AgentHumanNarrativeSplit } from "@/components/case-checking/humanReviewCompare";
import {
  HumanReviewStatusChip,
  agentGradeOverrideNote,
  caseReviewRollupGrade,
  humanReviewHelperText,
  type CaseCheckingGrade,
  type HumanReviewRouting,
} from "@/components/case-checking/caseCheckingStatusBadges";
import { Button } from "@/components/ui/button/Button";

export type { CaseCheckingGrade } from "@/components/case-checking/caseCheckingStatusBadges";

function agentReviewRoutingBannerClass(routing: HumanReviewRouting): string {
  switch (routing) {
    case "no_formal_review":
      return "border-l-emerald-500/80 bg-emerald-500/5 dark:bg-emerald-500/10";
    case "needs_human":
      return "border-l-amber-500/80 bg-amber-500/5 dark:bg-amber-500/10";
    case "priority_human":
      return "border-l-rose-500/90 bg-rose-500/10 dark:bg-rose-500/15";
    default:
      return "border-l-slate-400";
  }
}

function AgentReviewRoutingBanner({ routing }: { routing: HumanReviewRouting }) {
  const helper = humanReviewHelperText(routing);
  return (
    <div
      className={clsx(
        "mb-4 rounded-lg border-l-4 px-3 py-2.5 ring-1 ring-slate-200/80 dark:ring-white/5",
        agentReviewRoutingBannerClass(routing),
      )}
      data-testid="agent-review-routing-banner"
    >
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Agent review level</p>
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <HumanReviewStatusChip value={routing} size="panel" />
      </div>
      {helper ? <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{helper}</p> : null}
    </div>
  );
}

function GradeRationaleSpoiler({
  rationale,
  defaultOpen = false,
}: {
  rationale: string;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="mt-3" data-testid="grade-rationale-spoiler">
      <Button
        plain
        size="unstyled"
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="inline-flex !justify-start !px-0 text-left"
        aria-expanded={open}
        data-testid="grade-rationale-spoiler-toggle"
      >
        <span className="inline-flex items-center gap-1 leading-none">
          <span className={clsx(CC_SECTION_LABEL, "mb-0")}>Grade rationale</span>
          <FontAwesomeIcon
            icon={faChevronRight}
            className={clsx(
              "block h-3 w-3 shrink-0 -translate-y-1 text-slate-500 transition-transform",
              open && "rotate-90",
            )}
          />
        </span>
      </Button>
      {open ? (
        <p className="mt-2 text-sm leading-relaxed text-slate-400" data-testid="grade-rationale-spoiler-body">
          {rationale}
        </p>
      ) : null}
    </div>
  );
}

function HumanReviewPanel({ review }: { review: ApiCaseReview | null }) {
  if (!review?.human_review) return null;
  const helper = humanReviewHelperText(review.human_review);
  const reasons = review.human_review_reasons?.filter(Boolean) ?? [];

  return (
    <div className={CC_PANEL} data-testid="human-review-panel">
      <p className={clsx(CC_SECTION_LABEL, "mb-2")}>Case profile</p>
      <HumanReviewStatusChip value={review.human_review} size="panel" data-testid="human-review-label" />
      {helper ? <p className="mt-2 text-sm text-slate-400 leading-relaxed">{helper}</p> : null}
      {reasons.length > 0 ? (
        <ul className="mt-2 space-y-1 text-sm text-slate-400 list-disc pl-4">
          {reasons.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function CaseReviewOverviewPanel({
  grade,
  caseReview,
  displayAdviser,
  fallbackAdviser,
  showHumanReview = true,
  showAgentReviewRouting = false,
  gradeRationaleDefaultOpen = false,
  outcomeGrade,
  reviewAttribution,
  humanReviewNotes,
  agentGrade,
  ruleRollupGrade,
}: {
  grade: CaseCheckingGrade;
  caseReview: ApiCaseReview | null;
  displayAdviser?: string;
  fallbackAdviser?: string;
  showHumanReview?: boolean;
  /** Top banner: green / amber / red routine vs complex routing from the agent. */
  showAgentReviewRouting?: boolean;
  gradeRationaleDefaultOpen?: boolean;
  /** Human-reviewed overall grade; falls back to agent rollup when unset. */
  outcomeGrade?: CaseCheckingGrade;
  reviewAttribution?: ReviewAttribution | null;
  humanReviewNotes?: string | null;
  agentGrade?: CaseCheckingGrade;
  ruleRollupGrade?: CaseCheckingGrade | null;
}) {
  const agentGradeNote = agentGradeOverrideNote(caseReview?.baseline_grade, caseReview?.final_grade);
  const rollupGrade = outcomeGrade ?? caseReviewRollupGrade(caseReview, grade);
  const compareAgentGrade = agentGrade ?? caseReviewRollupGrade(caseReview, grade);
  const gradeRationale = caseReview?.grade_rationale?.trim() ?? "";

  const metaLine = (() => {
    if (reviewAttribution?.reviewedAt) {
      const reviewedOn = new Date(reviewAttribution.reviewedAt).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
      const reviewer = reviewAttribution.reviewerName?.trim();
      return reviewer ? `Reviewed on ${reviewedOn} by ${reviewer}` : `Reviewed on ${reviewedOn}`;
    }
    const parts = [
      displayAdviser || fallbackAdviser || null,
      caseReview?.reviewed_at
        ? `Agent graded ${new Date(caseReview.reviewed_at).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
          })}`
        : null,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(" · ") : null;
  })();

  return (
    <>
      <div className={CC_PANEL} data-testid="case-review-overview">
        {showAgentReviewRouting && caseReview?.human_review ? (
          <AgentReviewRoutingBanner routing={caseReview.human_review} />
        ) : null}
        <p className={clsx(CC_SECTION_LABEL, "mb-1")}>Overall outcome</p>
        <AgentHumanGradeCompare
          agentGrade={compareAgentGrade}
          humanGrade={rollupGrade}
          ruleRollupGrade={ruleRollupGrade}
        />
        {metaLine ? <p className="mt-1.5 text-xs text-slate-500">{metaLine}</p> : null}
        <p className={clsx(CC_SECTION_LABEL, "mt-3 mb-2")}>Assessment narrative</p>
        <AgentHumanNarrativeSplit
          agentSummary={caseReview?.summary}
          agentGradeNote={agentGradeNote}
          humanNotes={humanReviewNotes}
          humanGrade={rollupGrade}
          agentGrade={compareAgentGrade}
          hasHumanReview={Boolean(reviewAttribution?.reviewedAt)}
        />
        {gradeRationale ? (
          <GradeRationaleSpoiler rationale={gradeRationale} defaultOpen={gradeRationaleDefaultOpen} />
        ) : null}
      </div>
      {showHumanReview ? <HumanReviewPanel review={caseReview} /> : null}
    </>
  );
}
